import { useState, useMemo, useEffect, useRef } from 'react';

import { useSearchParams } from 'react-router-dom';
import { Plus, SlidersHorizontal, CheckSquare, Download, Sparkles, FileDown, ChevronRight, Images, ListFilter } from 'lucide-react';
import { AddItemDropdown } from '@/components/inventory/AddItemDropdown';
import { CollectionItemCard } from '@/components/item/CollectionItemCard';
import { InventoryListView } from '@/components/inventory/InventoryListView';
import { VirtualCardGrid } from '@/components/VirtualCardGrid';
import { VirtualGridBoundary } from '@/components/VirtualGridBoundary';


import { Button } from '@/components/ui/button';
import { SEO } from '@/components/SEO';
import { Header } from '@/components/Header';
import { PageShell } from '@/components/layout/PageShell';
import { InventoryPageShell } from '@/components/inventory/InventoryPageShell';
import { InventoryFilterPanel } from '@/components/inventory/InventoryFilterPanel';
import { MobileInventoryFilterSheet } from '@/components/inventory/MobileInventoryFilterSheet';
import {
  MobilePillSearchInput,
  MobileFilterPillButton,
  MobileSortPill,
} from '@/components/toolbar';
import { InventoryToolbar } from '@/components/inventory/InventoryToolbar';
import { InventoryViewToggle } from '@/components/inventory/InventoryViewToggle';
import { QuickListDialog } from '@/components/inventory/QuickListDialog';
import { QuickPriceEditDialog } from '@/components/inventory/QuickPriceEditDialog';

import { LoadingSpinner } from '@/components/ui/loading-spinner';



import { SelectionActionBar } from '@/components/inventory/SelectionActionBar';
import { DeleteItemsConfirmDialog } from '@/components/inventory/DeleteItemsConfirmDialog';
import { SignedOutInventory } from '@/components/inventory/SignedOutInventory';
import { SaveToMenuDialog } from '@/components/inventory/SaveToMenuDialog';
import { BulkListOnEbayDialog } from '@/components/inventory/BulkListOnEbayDialog';
import { INVENTORY_SORT_OPTIONS, INVENTORY_SHORTCUTS, isPendingSale, type StatusBucket } from '@/components/inventory/inventoryHelpers';
import { InventoryShortcuts } from '@/components/inventory/InventoryShortcuts';
import { useInventoryFilters } from '@/components/inventory/useInventoryFilters';
import { useInventorySelection } from '@/hooks/useInventorySelection';
import { useInventoryBulkActions } from '@/components/inventory/useInventoryBulkActions';
import { JobProgress } from '@/components/ui/JobProgress';
import { hasPendingCollectrPrice } from '@/lib/collectrPricing';
import { MVP_FEATURES } from '@/lib/mvpFeatures';

import { BulkListDialog } from '@/components/inventory/BulkListDialog';
import { BulkListPriceQueueDialog } from '@/components/inventory/BulkListPriceQueueDialog';
import { BulkPhotoUploadSheet } from '@/components/inventory/BulkPhotoUploadSheet';
import { RapidPhotographSheet } from '@/components/inventory/RapidPhotographSheet';
import { BulkPriceEditDialog } from '@/components/inventory/BulkPriceEditDialog';
import { SuggestPricesReviewDialog } from '@/components/inventory/SuggestPricesReviewDialog';
import { BulkMarkAsSoldSheet } from '@/components/inventory/BulkMarkAsSoldSheet';
import { ImportCollectrDialog } from '@/components/collection/ImportCollectrDialog';
import { StockImageReviewQueueDialog } from '@/components/inventory/StockImageReviewQueueDialog';
import { useIsMobile } from '@/hooks/use-mobile';
import { useUIStore } from '@/store/uiStore';
import { useMyItems } from '@/hooks/useUserItems';
import { useProfile } from '@/hooks/useProfile';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';
import { useItemInterestCounts } from '@/hooks/useItemInterestCounts';
import { useQueryClient } from '@tanstack/react-query';
import {
  deleteUserItem,
  deleteUserItems,
  duplicateUserItemAsDraft,
  updateUserItem,
} from '@/queries/userItems';
import { toast } from '@/lib/toast';
import { handleError } from '@/lib/handleError';
import { ItemVisibility, UserItem } from '@/types/userItem';
import { hasOnlyStockPhoto, STOCK_PHOTO_BLOCK_TITLE, STOCK_PHOTO_BLOCK_DESC } from '@/lib/listingEligibility';
import { useScrollRestoration } from '@/hooks/useScrollRestoration';

export default function P2PMarketplace() {
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    openAddItemModal,
    inventorySelectionRequested,
    clearInventorySelectionRequest,
    pendingEditItemId,
    clearEditItemRequest,
    collectrImportRequested,
    clearCollectrImportRequest,
    stockImageReviewRequested,
    clearStockImageReviewRequest,
    setItemsWithoutImagesCount,
    rapidListRequested,
    clearRapidListRequest,
    bulkPhotoUploadRequested,
    clearBulkPhotoUploadRequest,
    rapidPhotographRequested,
    clearRapidPhotographRequest,
    activeInventoryShortcut,
    setActiveInventoryShortcut,
    setPendingSalesQueueOpen,
  } = useUIStore();
  // List view retired — grid is the only display mode.
  const inventoryDisplayMode = 'grid' as 'grid' | 'list';

  // ========== DIALOG STATE ==========
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [scrollEl, setScrollEl] = useState<HTMLElement | null>(null);
  useScrollRestoration(scrollEl);

  const [updatingItems, setUpdatingItems] = useState<Set<string>>(new Set());
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [quickListItem, setQuickListItem] = useState<UserItem | null>(null);
  const [listViewEditItem, setListViewEditItem] = useState<UserItem | null>(null);
  const [showBulkListDialog, setShowBulkListDialog] = useState(false);
  const [bulkListScope, setBulkListScope] = useState<'selection' | 'filtered'>('filtered');
  const [showBulkPriceEditDialog, setShowBulkPriceEditDialog] = useState(false);
  const [showBulkSoldDialog, setShowBulkSoldDialog] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [showSaveToMenu, setShowSaveToMenu] = useState(false);
  const [showBulkListEbay, setShowBulkListEbay] = useState(false);
  const [showStockImageQueue, setShowStockImageQueue] = useState(false);
  const [showSuggestPrices, setShowSuggestPrices] = useState(false);
  const [isDuplicatingBulk, setIsDuplicatingBulk] = useState(false);
  const [bulkListPriceQueue, setBulkListPriceQueue] = useState<{
    unpriced: UserItem[];
    scoped: UserItem[];
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showBulkPhotoUpload, setShowBulkPhotoUpload] = useState(false);
  const [showRapidPhotograph, setShowRapidPhotograph] = useState(false);
  const [pendingBulkPhotoHandoff, setPendingBulkPhotoHandoff] = useState(false);

  // Auto-open import dialog if ?import=true
  useEffect(() => {
    if (searchParams.get('import') === 'true') {
      setImportDialogOpen(true);
      searchParams.delete('import');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // ?shortcut=pending_sales (from eBay sale notification click)
  useEffect(() => {
    const s = searchParams.get('shortcut');
    if (!s) return;
    const allowed = ['all', 'sold', 'pending_sales', 'unlisted', 'missing_photos', 'stale_prices', 'priced_no_offers'] as const;
    if ((allowed as readonly string[]).includes(s)) {
      setActiveInventoryShortcut(s as any);
      searchParams.delete('shortcut');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, setActiveInventoryShortcut]);

  // Cross-surface trigger (mobile Quick Create sheet)
  useEffect(() => {
    if (collectrImportRequested) {
      setImportDialogOpen(true);
      clearCollectrImportRequest();
    }
  }, [collectrImportRequested, clearCollectrImportRequest]);

  useEffect(() => {
    if (stockImageReviewRequested) {
      setShowStockImageQueue(true);
      clearStockImageReviewRequest();
    }
  }, [stockImageReviewRequested, clearStockImageReviewRequest]);

  // ========== DATA + FILTERS ==========
  const { data: myItemsRaw = [], isLoading } = useMyItems();
  const { profile } = useProfile();

  // Mirror selection mode into state so useInventoryFilters can freeze display
  // order while bulk actions are in progress (prevents just-actioned items from
  // jumping to a new sort position mid-flow).
  const [freezeInventoryOrder, setFreezeInventoryOrder] = useState(false);
  const filters = useInventoryFilters(myItemsRaw, freezeInventoryOrder, {
    includeSold: activeInventoryShortcut === 'sold',
  });
  const {
    activeTab, setActiveTab,
    categoryFilter, setCategoryFilter,
    conditionFilter, setConditionFilter,
    priceMin, setPriceMin,
    priceMax, setPriceMax,
    selectedGame, setSelectedGame,
    selectedLanguages, setSelectedLanguages,
    gradingCompany, setGradingCompany,
    gradeMin, setGradeMin,
    gradeMax, setGradeMax,
    searchQuery, setSearchQuery,
    sortBy, setSortBy,
    filteredItems: rawFilteredItems,
    publicListedIds,
    unlistedRawItems,
    unlistedFilteredItems,
    stats,
    itemsWithoutImagesCount,
    pricedItemsCount,
    mobileFilterCount,
    visibilityToggle,
    setVisibilityToggle,
    availableFilterOptions,
    clearFilters,
  } = filters;

  // Apply Inventory Shortcut preset filter on top of the standard filters.
  const filteredItems = useMemo(() => {
    const shortcut = INVENTORY_SHORTCUTS.find((s) => s.id === activeInventoryShortcut);
    if (!shortcut || shortcut.id === 'all') return rawFilteredItems;
    return rawFilteredItems.filter(shortcut.predicate);
  }, [rawFilteredItems, activeInventoryShortcut]);

  const pendingSaleCount = useMemo(
    () => (myItemsRaw as UserItem[]).filter(isPendingSale).length,
    [myItemsRaw],
  );


  // Queue/history shortcuts should not inherit active listing visibility filters.
  useEffect(() => {
    if (activeInventoryShortcut === 'pending_sales' && visibilityToggle === 'listed') {
      setVisibilityToggle('all');
    }
    if (activeInventoryShortcut === 'sold' && activeTab.length > 0) {
      setActiveTab([]);
    }
  }, [activeInventoryShortcut, activeTab.length, visibilityToggle, setActiveTab, setVisibilityToggle]);

  // The "Unlisted" chip drives the visibility pill so both surfaces agree.
  // Only reacts to shortcut transitions, so the pill stays user-controllable.
  const prevShortcutRef = useRef(activeInventoryShortcut);
  useEffect(() => {
    const prev = prevShortcutRef.current;
    if (prev === activeInventoryShortcut) return;
    prevShortcutRef.current = activeInventoryShortcut;
    if (activeInventoryShortcut === 'unlisted') {
      setVisibilityToggle('unlisted');
    } else if (prev === 'unlisted') {
      setVisibilityToggle('all');
    }
  }, [activeInventoryShortcut, setVisibilityToggle]);

  // Sync photoless count to global UI store so the bottom-nav Quick Create sheet
  // can show/hide the "Add stock photos" action outside the Inventory page.
  useEffect(() => {
    setItemsWithoutImagesCount(itemsWithoutImagesCount);
  }, [itemsWithoutImagesCount, setItemsWithoutImagesCount]);

  // Get interest counts for listed items
  const { data: interestCounts = {} } = useItemInterestCounts(publicListedIds);


  // ========== SELECTION ==========
  const selection = useInventorySelection(filteredItems);
  const {
    isSelectionMode,
    selectedItemIds,
    handleEnterSelectionMode,
    handleExitSelectionMode,
    clearSelection,
    handleToggleSelection,
    handleSelectAll,
    handleDeselectAll,
  } = selection;

  useEffect(() => {
    setFreezeInventoryOrder(isSelectionMode);
  }, [isSelectionMode]);

  // Consume Select-Items request from the + bottom nav menu
  useEffect(() => {
    if (inventorySelectionRequested && user) {
      handleEnterSelectionMode();
      clearInventorySelectionRequest();
    }
  }, [inventorySelectionRequested, user, handleEnterSelectionMode, clearInventorySelectionRequest]);

  // Consume Rapid List request from the + bottom nav menu (mobile)
  // Wait until items have loaded so we don't silently no-op on cold nav.
  useEffect(() => {
    if (!rapidListRequested || !user || isLoading) return;
    if (unlistedRawItems.length) {
      setBulkListPriceQueue({ unpriced: unlistedRawItems, scoped: unlistedRawItems });
    } else {
      toast('Nothing to rapid list — every item is already listed.');
    }
    clearRapidListRequest();
  }, [rapidListRequested, user, isLoading, unlistedRawItems, clearRapidListRequest]);

  // Consume Bulk Photo Upload request from the + bottom nav menu.
  useEffect(() => {
    if (!bulkPhotoUploadRequested || !user) return;
    setShowBulkPhotoUpload(true);
    clearBulkPhotoUploadRequest();
  }, [bulkPhotoUploadRequested, user, clearBulkPhotoUploadRequest]);

  // Consume Rapid Photograph request from the + bottom nav menu.
  useEffect(() => {
    if (!rapidPhotographRequested || !user) return;
    setShowRapidPhotograph(true);
    clearRapidPhotographRequest();
  }, [rapidPhotographRequested, user, clearRapidPhotographRequest]);

  useEffect(() => {
    if (!pendingBulkPhotoHandoff || showRapidPhotograph) return;
    const frame = window.requestAnimationFrame(() => {
      setPendingBulkPhotoHandoff(false);
      setShowBulkPhotoUpload(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, [pendingBulkPhotoHandoff, showRapidPhotograph]);



  // Consume pending edit-item request (e.g. after duplicating)
  useEffect(() => {
    if (!pendingEditItemId) return;
    const target = myItemsRaw.find((i: UserItem) => i.id === pendingEditItemId);
    if (target) {
      setListViewEditItem(target);
      clearEditItemRequest();
    }
  }, [pendingEditItemId, myItemsRaw, clearEditItemRequest]);

  // Clear selection when switching grid <-> list (visual context resets)
  useEffect(() => {
    if (isSelectionMode) {
      handleDeselectAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inventoryDisplayMode]);

  // One-time mobile tip about long-press
  useEffect(() => {
    if (!isMobile || !user) return;
    try {
      if (localStorage.getItem('cs:long-press-hint-seen') === '1') return;
      const t = window.setTimeout(() => {
        toast('Tip: long-press an item to multi-select', { duration: 4500 });
        localStorage.setItem('cs:long-press-hint-seen', '1');
      }, 1200);
      return () => window.clearTimeout(t);
    } catch { /* ignore */ }
  }, [isMobile, user]);

  // ========== BULK ACTIONS ==========
  const bulkActions = useInventoryBulkActions({
    filteredItems,
    selectedItemIds,
    clearSelection,
    setShowBulkListDialog,
    setShowBulkPriceEditDialog,
    setBulkListScope,
  });
  const {
    isBulkProcessing,
    bulkProgress,
    handleSelectedListForSale,
    handleSelectedAcceptOffers,
    handleSelectedUnlist,
    handleSelectedPriceFromCollectr,
    handleBulkVisibilityChange,
    handleBulkStockImages,
    handleBulkPriceEdit,
    handleBulkListConfirm,
  } = bulkActions;

  // Selection summary
  const selectionSummary = useMemo(() => {
    const items = filteredItems.filter(item => selectedItemIds.has(item.id));
    const listed = items.filter(i => i.visibility === 'listed').length;
    const unlistedItems = items.filter(i => i.visibility !== 'listed');
    const unlisted = unlistedItems.length;
    const pricedUnlisted = unlistedItems.filter(
      i => (i.asking_price_cents ?? 0) > 0 || !!i.is_negotiable,
    ).length;
    const acceptingOffers = items.filter(i => !!i.is_negotiable).length;
    const collectrPriceable = items.filter(i => hasPendingCollectrPrice(i)).length;
    return { total: items.length, listed, unlisted, pricedUnlisted, acceptingOffers, collectrPriceable };
  }, [filteredItems, selectedItemIds]);

  

  // ========== SINGLE-ITEM HANDLERS ==========
  const handleBulkDeleteClick = () => {
    if (selectedItemIds.size === 0) return;
    setShowBulkDeleteConfirm(true);
  };

  const handleBulkDeleteConfirm = async () => {
    if (selectedItemIds.size === 0) return;
    setIsDeleting(true);
    const itemIds = Array.from(selectedItemIds);
    try {
      const { endEbayListingsForItems } = await import('@/lib/endEbayListingsForItems');
      await endEbayListingsForItems(itemIds);
      await deleteUserItems(itemIds);
      queryClient.invalidateQueries({ queryKey: ['my-items', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      itemIds.forEach(id => queryClient.invalidateQueries({ queryKey: ['user-item', id] }));
      toast.success(`${itemIds.length} item${itemIds.length !== 1 ? 's' : ''} deleted`);
      clearSelection();
    } catch (error) {
      handleError(error, { context: 'inventory.bulkDelete', userMessage: 'Failed to delete items', tone: 'destructive' });
    } finally {
      setIsDeleting(false);
      setShowBulkDeleteConfirm(false);
    }
  };

  const handleBulkDuplicate = async () => {
    if (selectedItemIds.size === 0 || isDuplicatingBulk) return;
    const ids = Array.from(selectedItemIds);
    if (ids.length > 20) {
      toast.error('Duplicate up to 20 items at a time.');
      return;
    }
    setIsDuplicatingBulk(true);
    let ok = 0;
    let fail = 0;
    for (const id of ids) {
      const source = myItemsRaw.find((i: UserItem) => i.id === id);
      if (!source) { fail += 1; continue; }
      try {
        await duplicateUserItemAsDraft(source, user!.id);
        ok += 1;
      } catch { fail += 1; }
    }
    setIsDuplicatingBulk(false);
    queryClient.invalidateQueries({ queryKey: ['my-items', user?.id] });
    if (ok) toast.success(`${ok} duplicate${ok === 1 ? '' : 's'} created (as private drafts)`);
    if (fail) toast.error(`${fail} duplicate${fail === 1 ? '' : 's'} failed`);
    clearSelection();
  };

  const handleVisibilityChange = async (itemId: string, newVisibility: ItemVisibility) => {
    if (newVisibility === 'listed') {
      const item = myItemsRaw.find((i: UserItem) => i.id === itemId);
      if (item) {
        if (hasOnlyStockPhoto(item)) {
          toast.error(STOCK_PHOTO_BLOCK_TITLE, { description: STOCK_PHOTO_BLOCK_DESC });
          return;
        }
        setQuickListItem(item);
        return;
      }
    }

    setUpdatingItems(prev => new Set(prev).add(itemId));
    try {
      const updateData: { visibility: ItemVisibility; is_listed: boolean } = {
        visibility: newVisibility,
        is_listed: newVisibility === 'listed',
      };
      await updateUserItem(itemId, updateData);
      queryClient.invalidateQueries({ queryKey: ['my-items', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-item', itemId] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      toast.success(`Item visibility updated to "${newVisibility}"`);
    } catch (error) {
      handleError(error, { context: 'inventory.updateVisibility', userMessage: 'Failed to update visibility', tone: 'destructive' });
    } finally {
      setUpdatingItems(prev => {
        const next = new Set(prev);
        next.delete(itemId);
        return next;
      });
    }
  };

  const handleDeleteClick = (itemId: string) => setItemToDelete(itemId);

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    try {
      const { endEbayListingsForItems } = await import('@/lib/endEbayListingsForItems');
      await endEbayListingsForItems([itemToDelete]);
      await deleteUserItem(itemToDelete);
      queryClient.invalidateQueries({ queryKey: ['my-items', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['user-item', itemToDelete] });
      queryClient.invalidateQueries({ queryKey: ['marketplace-items'] });
      toast.success('Item deleted');
    } catch (error) {
      handleError(error, { context: 'inventory.deleteItem', userMessage: 'Failed to delete item', tone: 'destructive' });
    } finally {
      setItemToDelete(null);
    }
  };

  if (!user) {
    return (
      <PageShell scroll="none" className="relative">
        <SEO
          title="Inventory | CardSimp"
          description="Sign in to manage your TCG inventory on CardSimp."
          noindex
        />
        <Header />
        <SignedOutInventory />
      </PageShell>
    );
  }

  const addItemExtras = [
    {
      key: 'collectr-import',
      label: 'Import from Collectr',
      icon: <Download className="w-4 h-4 text-active-gold" />,
      description: 'Sync your Collectr collection',
      onSelect: () => setImportDialogOpen(true),
    },
    {
      key: 'stock-photos',
      label: 'Add stock photos',
      icon: <Images className="w-4 h-4 text-active-gold" />,
      description: itemsWithoutImagesCount > 0
        ? `Review and apply stock photos to ${itemsWithoutImagesCount} item${itemsWithoutImagesCount === 1 ? '' : 's'}`
        : 'No items need stock photos',
      disabled: itemsWithoutImagesCount === 0,
      onSelect: () => setShowStockImageQueue(true),
    },
    {
      key: 'select',
      label: 'Select items…',
      icon: <CheckSquare className="w-4 h-4 text-active-gold" />,
      description: 'Bulk list, unlist, delete, list on eBay, or save as a menu',
      disabled: !filteredItems.length,
      onSelect: handleEnterSelectionMode,
    },
  ];

  return (
    <InventoryPageShell
      onScrollElementChange={setScrollEl}
      contentPadding={inventoryDisplayMode === 'list' && !isLoading ? 'list' : 'default'}
      desktopToolbar={
        <>
          <div className="flex-1 min-w-0">
            <InventoryToolbar
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
              sortBy={sortBy}
              setSortBy={setSortBy}
              filteredCount={filteredItems.length}
              pricedItemsCount={pricedItemsCount}
              onBulkAction={handleBulkVisibilityChange}
              onBulkStockImages={handleBulkStockImages}
              onBulkPriceEdit={() => setShowBulkPriceEditDialog(true)}
              onRapidList={() => {
                if (!unlistedFilteredItems.length) return;
                setBulkListPriceQueue({ unpriced: unlistedFilteredItems, scoped: unlistedFilteredItems });
              }}
              rapidListCount={unlistedFilteredItems.length}
              onBulkPhotoUpload={() => setShowRapidPhotograph(true)}
              onEnterSelectionMode={handleEnterSelectionMode}
              isBulkProcessing={isBulkProcessing}
              showBulkActions={!!user}
              isSelectionMode={isSelectionMode}
              selectedCount={selectedItemIds.size}
              totalCount={filteredItems.length}
              onCancelSelection={handleExitSelectionMode}
              onDeleteSelected={handleBulkDeleteClick}
              onListSelected={handleSelectedListForSale}
              onPrivateSelected={handleSelectedUnlist}
              onAcceptOffersSelected={handleSelectedAcceptOffers}
              onSaveSelectedToMenu={MVP_FEATURES.menus ? (() => selectedItemIds.size > 0 && setShowSaveToMenu(true)) : undefined}
              onListSelectedOnEbay={() => selectedItemIds.size > 0 && setShowBulkListEbay(true)}
              onPriceFromCollectrSelected={handleSelectedPriceFromCollectr}
              onSuggestPricesSelected={() => selectedItemIds.size > 0 && setShowSuggestPrices(true)}
              onMarkSoldSelected={() => selectedItemIds.size > 0 && setShowBulkSoldDialog(true)}
              onDuplicateSelected={handleBulkDuplicate}
              selectionSummary={selectionSummary}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              username={profile?.username}
              listedCount={stats.listed}
              inventoryTotal={stats.total}
              visibility={visibilityToggle}
              onVisibilityChange={setVisibilityToggle}
            />
          </div>
          {user && !isSelectionMode && <AddItemDropdown extraItems={addItemExtras} />}
        </>
      }
      desktopSidebar={
        <InventoryFilterPanel
          mode="my-items"
          category={categoryFilter}
          setCategory={setCategoryFilter}
          condition={conditionFilter}
          setCondition={setConditionFilter}
          priceMin={priceMin}
          setPriceMin={setPriceMin}
          priceMax={priceMax}
          setPriceMax={setPriceMax}
          location={null}
          setLocation={() => {}}
          distanceRadius={25}
          setDistanceRadius={() => {}}
          includeShipping={false}
          setIncludeShipping={() => {}}
          gradingCompany={gradingCompany}
          setGradingCompany={setGradingCompany}
          gradeMin={gradeMin}
          setGradeMin={setGradeMin}
          gradeMax={gradeMax}
          setGradeMax={setGradeMax}
          premiumOnly={false}
          setPremiumOnly={() => {}}
          productType="all"
          setProductType={() => {}}
          selectedGame={selectedGame}
          setSelectedGame={setSelectedGame}
          selectedLanguages={selectedLanguages}
          setSelectedLanguages={setSelectedLanguages}
          visibility={activeTab}
          setVisibility={(v) => setActiveTab(v as StatusBucket[])}
          {...availableFilterOptions}
          onClearFilters={clearFilters}
        />
      }
      mobileToolbar={
        <>
          <MobilePillSearchInput
            placeholder={stats.total > 0 ? `Search ${stats.total} item${stats.total === 1 ? '' : 's'}…` : 'Search inventory…'}
            value={searchQuery}
            onChange={setSearchQuery}
            rightHint={
              stats.listed > 0 || stats.private > 0
                ? [
                    stats.listed > 0 ? `${stats.listed} listed` : null,
                    stats.private > 0 ? `${stats.private} unlisted` : null,
                  ].filter(Boolean).join(', ')
                : undefined
            }
          />

          <div className="flex items-center gap-2 mt-2">
            <MobileInventoryFilterSheet
              mode="my-items"
              category={categoryFilter}
              setCategory={setCategoryFilter}
              condition={conditionFilter}
              setCondition={setConditionFilter}
              priceMin={priceMin}
              setPriceMin={setPriceMin}
              priceMax={priceMax}
              setPriceMax={setPriceMax}
              location={null}
              setLocation={() => {}}
              distanceRadius={25}
              setDistanceRadius={() => {}}
              includeShipping={false}
              setIncludeShipping={() => {}}
              gradingCompany={gradingCompany}
              setGradingCompany={setGradingCompany}
              gradeMin={gradeMin}
              setGradeMin={setGradeMin}
              gradeMax={gradeMax}
              setGradeMax={setGradeMax}
              premiumOnly={false}
              setPremiumOnly={() => {}}
              productType="all"
              setProductType={() => {}}
              selectedGame={selectedGame}
              setSelectedGame={setSelectedGame}
              selectedLanguages={selectedLanguages}
              setSelectedLanguages={setSelectedLanguages}
              visibility={activeTab}
              setVisibility={(v) => setActiveTab(v as StatusBucket[])}
              {...availableFilterOptions}
              onClearFilters={clearFilters}
              trigger={
                <MobileFilterPillButton
                  label="Filters"
                  icon={SlidersHorizontal}
                  count={mobileFilterCount}
                  active={mobileFilterCount > 0}
                />
              }
            />

            <MobileSortPill<typeof sortBy>
              value={sortBy}
              options={INVENTORY_SORT_OPTIONS}
              defaultValue="newest"
              onChange={setSortBy}
            />

            <MobileFilterPillButton
              label={visibilityToggle === 'all' ? 'All items' : visibilityToggle === 'listed' ? 'Listed only' : 'Unlisted only'}
              icon={ListFilter}
              active={visibilityToggle !== 'all'}
              onClick={() => {
                const next: Record<typeof visibilityToggle, typeof visibilityToggle> = {
                  all: 'listed',
                  listed: 'unlisted',
                  unlisted: 'all',
                };
                setVisibilityToggle(next[visibilityToggle]);
              }}
            />

            <InventoryViewToggle className="ml-auto" />

            {user && !isSelectionMode && (
              <div className="hidden md:block">
                <AddItemDropdown extraItems={addItemExtras} />
              </div>
            )}
          </div>
        </>
      }
      overlays={
        <>
          <DeleteItemsConfirmDialog
            open={!!itemToDelete}
            onOpenChange={(open) => !open && setItemToDelete(null)}
            count={1}
            itemTitle={myItemsRaw.find((i: UserItem) => i.id === itemToDelete)?.title}
            onConfirm={handleDeleteConfirm}
          />

          <QuickListDialog
            item={quickListItem}
            open={!!quickListItem}
            onOpenChange={(open) => !open && setQuickListItem(null)}
            onSuccess={() => queryClient.invalidateQueries({ queryKey: ['my-items'] })}
          />

          {listViewEditItem && (
            <QuickPriceEditDialog
              item={listViewEditItem}
              open={!!listViewEditItem}
              onClose={() => setListViewEditItem(null)}
            />
          )}

          <BulkListDialog
            open={showBulkListDialog}
            onOpenChange={(o) => {
              setShowBulkListDialog(o);
              if (!o) setBulkListScope('filtered');
            }}
            items={
              bulkListScope === 'selection'
                ? myItemsRaw.filter((i: UserItem) => selectedItemIds.has(i.id))
                : filteredItems
            }
            onNeedsPricing={(unpriced) => {
              const scoped =
                bulkListScope === 'selection'
                  ? myItemsRaw.filter((i: UserItem) => selectedItemIds.has(i.id))
                  : filteredItems;
              setShowBulkListDialog(false);
              setTimeout(() => {
                setBulkListPriceQueue({ unpriced, scoped });
              }, 220);
            }}
            onConfirm={async (enableOffers) => {
              const scopedItems =
                bulkListScope === 'selection'
                  ? myItemsRaw.filter((i: UserItem) => selectedItemIds.has(i.id))
                  : filteredItems;
              await handleBulkListConfirm(null, enableOffers, scopedItems);
              if (bulkListScope === 'selection') handleExitSelectionMode();
              setBulkListScope('filtered');
            }}
            isProcessing={isBulkProcessing}
          />

          {bulkListPriceQueue && (
            <BulkListPriceQueueDialog
              open={!!bulkListPriceQueue}
              onOpenChange={(o) => {
                if (!o) {
                  setBulkListPriceQueue(null);
                  setBulkListScope('filtered');
                }
              }}
              items={bulkListPriceQueue.unpriced}
              alreadyPricedCount={
                bulkListPriceQueue.scoped.filter(
                  (i) => i.asking_price_cents && i.asking_price_cents > 0,
                ).length
              }
              isProcessing={isBulkProcessing}
              onDecision={async (item, decision) => {
                if (decision.kind === 'skip') return;
                const update: Record<string, unknown> = {
                  visibility: 'listed',
                  is_listed: true,
                };
                if (decision.kind === 'price') {
                  update.asking_price_cents = decision.priceCents;
                  if (decision.acceptsOffers) update.is_negotiable = true;
                } else {
                  update.is_negotiable = true;
                }
                try {
                  await updateUserItem(item.id, update);
                } catch (error) {
                  console.error('autosave list failed', error);
                  toast.error(`Failed to list ${item.title}`);
                }
              }}
              onTitleChange={async (item, title) => {
                try {
                  await updateUserItem(item.id, { title });
                  queryClient.invalidateQueries({ queryKey: ['my-items'] });
                } catch (error) {
                  console.error('title update failed', error);
                  toast.error('Failed to update title');
                }
              }}
              onComplete={async (decisions) => {
                const priced = Array.from(decisions.values()).filter((d) => d.kind === 'price').length;
                const offers = Array.from(decisions.values()).filter((d) => d.kind === 'offers').length;
                const total = priced + offers;
                queryClient.invalidateQueries({ queryKey: ['my-items'] });
                if (total > 0) toast.success(`${total} item${total === 1 ? '' : 's'} listed`);
                if (bulkListScope === 'selection') handleExitSelectionMode();
                setBulkListPriceQueue(null);
                setBulkListScope('filtered');
              }}
            />
          )}

          <BulkPhotoUploadSheet
            open={showBulkPhotoUpload && !showRapidPhotograph}
            onOpenChange={(open) => {
              setShowBulkPhotoUpload(open);
              if (!open) setPendingBulkPhotoHandoff(false);
            }}
            onCreated={(created) => {
              if (!created.length) return;
              setBulkListPriceQueue({ unpriced: created, scoped: created });
            }}
          />

          {showRapidPhotograph || pendingBulkPhotoHandoff ? (
            <RapidPhotographSheet
              open={showRapidPhotograph}
              onOpenChange={(open) => {
                setShowRapidPhotograph(open);
                if (open) setPendingBulkPhotoHandoff(false);
              }}
              allItems={myItemsRaw as UserItem[]}
              onSwitchToGallery={() => {
                setPendingBulkPhotoHandoff(true);
                setShowRapidPhotograph(false);
              }}
            />
          ) : null}


          <BulkPriceEditDialog
            open={showBulkPriceEditDialog}
            onOpenChange={setShowBulkPriceEditDialog}
            filteredCount={filteredItems.length}
            pricedItemsCount={pricedItemsCount}
            onConfirm={handleBulkPriceEdit}
            isProcessing={isBulkProcessing}
          />

          <BulkMarkAsSoldSheet
            open={showBulkSoldDialog}
            onOpenChange={setShowBulkSoldDialog}
            selectedItems={myItemsRaw.filter((item: UserItem) => selectedItemIds.has(item.id))}
            onSuccess={clearSelection}
          />

          <ImportCollectrDialog
            open={importDialogOpen}
            onOpenChange={setImportDialogOpen}
            onImportComplete={() => {
              queryClient.invalidateQueries({ queryKey: ['my-items'] });
              queryClient.invalidateQueries({ queryKey: ['inventory'] });
            }}
          />

          <StockImageReviewQueueDialog
            open={showStockImageQueue}
            onOpenChange={setShowStockImageQueue}
            items={(isSelectionMode && selectedItemIds.size > 0
              ? filteredItems.filter((it) => selectedItemIds.has(it.id))
              : filteredItems
            ).filter(
              (it) => !it.images?.[0]?.image_url && !it.stock_image_url,
            )}
          />

          {isSelectionMode && (
            <SelectionActionBar
              selectedCount={selectedItemIds.size}
              totalCount={filteredItems.length}
              onCancel={handleExitSelectionMode}
              onSelectAll={handleSelectAll}
              onDeselectAll={handleDeselectAll}
              onListSelected={handleSelectedListForSale}
              onPrivateSelected={handleSelectedUnlist}
              onAcceptOffersSelected={handleSelectedAcceptOffers}
              onSaveToMenu={MVP_FEATURES.menus ? (() => selectedItemIds.size > 0 && setShowSaveToMenu(true)) : undefined}
              onAddStockPhotos={() => setShowStockImageQueue(true)}
              onListOnEbay={() => selectedItemIds.size > 0 && setShowBulkListEbay(true)}
              onSuggestPrices={() => selectedItemIds.size > 0 && setShowSuggestPrices(true)}
              onMarkSold={() => selectedItemIds.size > 0 && setShowBulkSoldDialog(true)}
              onDuplicateSelected={handleBulkDuplicate}
              selectionSummary={selectionSummary}
              onDeleteSelected={handleBulkDeleteClick}
              isProcessing={isBulkProcessing}
            />
          )}

          {MVP_FEATURES.menus && (
            <SaveToMenuDialog
              open={showSaveToMenu}
              onOpenChange={setShowSaveToMenu}
              itemIds={Array.from(selectedItemIds)}
              onDone={handleExitSelectionMode}
            />
          )}

          <BulkListOnEbayDialog
            open={showBulkListEbay}
            onOpenChange={setShowBulkListEbay}
            items={myItemsRaw.filter((item: UserItem) => selectedItemIds.has(item.id))}
            onDone={handleExitSelectionMode}
          />

          <DeleteItemsConfirmDialog
            open={showBulkDeleteConfirm}
            onOpenChange={setShowBulkDeleteConfirm}
            count={selectedItemIds.size}
            isDeleting={isDeleting}
            onConfirm={handleBulkDeleteConfirm}
          />

          <SuggestPricesReviewDialog
            open={showSuggestPrices}
            onOpenChange={setShowSuggestPrices}
            items={myItemsRaw.filter((item: UserItem) => selectedItemIds.has(item.id))}
            onDone={handleExitSelectionMode}
          />
        </>
      }
    >
      {/* Tier 2: inline determinate progress for bulk actions (visible on mobile,
          where progress toasts are suppressed). */}
      {bulkProgress && (
        <div className="px-3 md:px-6 pt-2">
          <JobProgress
            label={bulkProgress.label}
            done={bulkProgress.done}
            total={bulkProgress.total}
            failed={bulkProgress.failed}
            isRunning={bulkProgress.done < bulkProgress.total}
          />
        </div>
      )}

      {/* Loading State — only block on truly cold first load. */}
      {isLoading && myItemsRaw.length === 0 && (
        <div className="flex justify-center py-24"><LoadingSpinner variant="panel" /></div>
      )}


      {(!isLoading || myItemsRaw.length > 0) && (
        <main className={cn(
          'animate-fade-in pt-1',
          inventoryDisplayMode === 'list' && 'flex-1 flex flex-col min-h-0',
        )}>
          {user && myItemsRaw.length > 0 && (
            <InventoryShortcuts
              activeId={activeInventoryShortcut}
              onSelect={setActiveInventoryShortcut}
              allItems={myItemsRaw as UserItem[]}
            />
          )}

          {pendingSaleCount > 0 && (
            <div className="px-3 md:px-6 pt-2">
              <button
                type="button"
                onClick={() => setPendingSalesQueueOpen(true)}
                className="w-full flex items-center justify-between gap-3 rounded-xl border border-active-gold/40 bg-active-gold/[0.08] px-3 py-2.5 text-left hover:bg-active-gold/[0.14] transition-colors"
              >
                <span className="min-w-0">
                  <span className="block text-[13px] font-semibold text-active-gold">
                    {pendingSaleCount} pending eBay {pendingSaleCount === 1 ? 'sale' : 'sales'}
                  </span>
                  <span className="block text-[11px] text-muted-foreground truncate">
                    Confirm to move them to Sold and out of active inventory.
                  </span>
                </span>
                <span className="shrink-0 rounded-full bg-active-gold px-3 py-1 text-[12px] font-semibold text-black">
                  Review
                </span>
              </button>
            </div>
          )}
          <div className={cn(
            'px-3 md:px-6 pt-2',
            inventoryDisplayMode === 'list' && 'flex-1 flex flex-col min-h-0',
          )}>
          {filteredItems.length === 0 ? (
            myItemsRaw.length === 0 ? (
              <div className="mx-auto max-w-md py-4 sm:py-5 px-1">
                <div className="mb-6 sm:mb-8 text-left">
                  <h3 className="text-[22px] sm:text-2xl font-semibold tracking-tight leading-tight">
                    Let's add Inventory
                  </h3>
                  <p className="text-[13px] text-muted-foreground mt-1.5">
                    Pick whatever, mix and match later.
                  </p>
                </div>
                <div className="rounded-2xl border border-hairline bg-surface-1 overflow-hidden divide-y divide-hairline">
                  <button
                    type="button"
                    onClick={() => setImportDialogOpen(true)}
                    className="w-full flex items-center gap-3.5 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.03] active:bg-white/[0.05]"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-active-gold">
                      <FileDown className="h-[18px] w-[18px]" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[15px] font-medium text-foreground">Import from Collectr</span>
                        <span className="text-[10px] font-semibold uppercase tracking-wider text-active-gold/90">Fastest</span>
                      </div>
                      <p className="text-[12.5px] text-muted-foreground mt-0.5 truncate">
                        Sync your entire collection in seconds
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0" />
                  </button>
                  <button
                    type="button"
                    onClick={openAddItemModal}
                    className="w-full flex items-center gap-3.5 px-4 py-3.5 text-left transition-colors hover:bg-white/[0.03] active:bg-white/[0.05]"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-white/[0.06] text-foreground/80">
                      <Plus className="h-[18px] w-[18px]" strokeWidth={2} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <span className="text-[15px] font-medium text-foreground">Quick add</span>
                      <p className="text-[12.5px] text-muted-foreground mt-0.5 truncate">
                        One card at a time
                      </p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-muted-foreground/60 shrink-0" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="text-center py-20">
                <h3 className="text-xl font-semibold mb-2">No matching items</h3>
                <p className="text-muted-foreground mb-6">Try adjusting your filters.</p>
                <Button variant="outline" onClick={clearFilters}>Clear Filters</Button>
              </div>
            )
          ) : inventoryDisplayMode === 'list' ? (
            <InventoryListView
              items={filteredItems}
              onItemClick={(item) => setListViewEditItem(item)}
              onVisibilityChange={handleVisibilityChange}
              updatingItems={updatingItems}
              isSelectionMode={isSelectionMode}
              selectedItemIds={selectedItemIds}
              onToggleSelection={handleToggleSelection}
              onEnterSelectionMode={handleEnterSelectionMode}
            />
          ) : (
            <div className="inventory-grid-wrapper min-h-0 flex flex-col">
              {(() => {
                const plainGrid = (
                  <div className="inventory-grid">
                    {filteredItems.map((item: UserItem, index: number) => (
                      <CollectionItemCard
                        key={item.id}
                        item={item}
                        onVisibilityChange={handleVisibilityChange}
                        onDelete={handleDeleteClick}
                        isUpdating={updatingItems.has(item.id)}
                        interestCount={interestCounts[item.id] || 0}
                        isSelectionMode={isSelectionMode}
                        isSelected={selectedItemIds.has(item.id)}
                        onToggleSelection={handleToggleSelection}
                        onEnterSelectionMode={handleEnterSelectionMode}
                        priority={index < (isMobile ? 6 : 12)}
                      />
                    ))}
                  </div>
                );
                if (filteredItems.length <= (isMobile ? 60 : 100)) return plainGrid;
                return (
                  <VirtualGridBoundary
                    resetKey={`${filteredItems.length}|${visibilityToggle}|${activeTab.join(',')}`}
                    fallback={plainGrid}
                  >
                    <VirtualCardGrid
                      key={`${visibilityToggle}|${activeTab.join(',')}|${searchQuery}|${categoryFilter.join(",")}|${conditionFilter}|${selectedGame}|${selectedLanguages.join(',')}|${priceMin}-${priceMax}|${gradingCompany}|${gradeMin}-${gradeMax}|${sortBy}|${filteredItems.length}`}
                      items={filteredItems}
                      gridClassName="inventory-grid"
                      customScrollParent={scrollEl}
                      renderItem={(item: UserItem, index: number) => (
                        <CollectionItemCard
                          item={item}
                          onVisibilityChange={handleVisibilityChange}
                          onDelete={handleDeleteClick}
                          isUpdating={updatingItems.has(item.id)}
                          interestCount={interestCounts[item.id] || 0}
                          isSelectionMode={isSelectionMode}
                          isSelected={selectedItemIds.has(item.id)}
                          onToggleSelection={handleToggleSelection}
                          onEnterSelectionMode={handleEnterSelectionMode}
                          priority={index < (isMobile ? 6 : 12)}
                        />
                      )}
                    />
                  </VirtualGridBoundary>
                );
              })()}
            </div>
          )}
          </div>
        </main>
      )}
    </InventoryPageShell>
  );
}
